import React, { useState, useEffect, useCallback } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import { scrollToSection } from "../lib/scrollToSection";
import { useSettings } from "../lib/settings";
import { leadFormCopy } from "../lib/leadForm";

const LOGO_COLOR = "/assets/logo-fped-color.svg";

const NAV_LINKS = [
  { id: "benefits", label: "Beneficii" },
  { id: "curriculum", label: "Programa" },
  { id: "prima", label: "Prima de carieră" },
  { id: "pricing", label: "Preț" },
  { id: "grupe", label: "Orar" },
  { id: "faq", label: "Întrebări" },
];

// M910 — every CTA / nav click pre-suppresses the next popup firing.
// Nav-bar links to non-form sections (Beneficii, Programa, etc.) are also
// suppressed because clicking them is an explicit engagement signal — the
// user is exploring intentionally; surfacing a popup mid-scroll is annoying.
const scrollTo = (id) => scrollToSection(id);

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  // "Cere detalii" or "Cere broșura", matching what the form will deliver.
  const leadCta = leadFormCopy(useSettings().lead_form_mode).headerCta;

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const goTo = useCallback((id) => {
    closeMobile();
    // Defer scroll so the sheet animation finishes first (no jank).
    requestAnimationFrame(() => scrollTo(id));
  }, [closeMobile]);

  // Close on Escape + lock body scroll while the menu is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => { if (e.key === "Escape") closeMobile(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileOpen, closeMobile]);

  return (
    <>
      <header
        data-testid="site-header"
        className="sticky top-0 z-40 glass border-b border-[#E5E7EB]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center group" data-testid="brand-link" aria-label="Fundația pentru Educație Digitală – pagina principală">
            <img
              src={LOGO_COLOR}
              alt="Fundația pentru Educație Digitală"
              className="h-12 md:h-14 w-auto object-contain transition-transform group-hover:scale-[1.03]"
            />
          </a>

          <nav className="hidden md:flex items-center gap-7 text-sm text-[#373A45]/80">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                className="hover:text-[#965EA7] transition-colors"
                data-testid={`nav-${l.id}`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              className="hidden sm:inline-flex rounded-full text-[#373A45] hover:bg-[#F0E7F4] hover:text-[#965EA7]"
              onClick={() => scrollTo("lead-form")}
              data-testid="header-lead-cta"
            >
              {leadCta}
            </Button>
            <Button
              className="hidden sm:inline-flex rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white shadow-[0_4px_14px_0_rgba(150,94,167,0.39)] px-5"
              onClick={() => scrollTo("pricing")}
              data-testid="header-enroll-cta"
            >
              Înscrie-te
            </Button>
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden p-2 rounded-md text-[#373A45] hover:bg-[#F0E7F4] transition-colors"
              aria-label={mobileOpen ? "Închide meniul" : "Deschide meniul"}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav-sheet"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile nav sheet — outside the header so it can sit above sticky/glass siblings */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-50"
          role="dialog"
          aria-modal="true"
          aria-label="Meniu principal"
          data-testid="mobile-nav-sheet-root"
        >
          {/* Backdrop */}
          <button
            type="button"
            onClick={closeMobile}
            aria-label="Închide meniul"
            className="absolute inset-0 bg-[#373A45]/55 backdrop-blur-sm"
            data-testid="mobile-nav-backdrop"
          />
          {/* Panel */}
          <div
            id="mobile-nav-sheet"
            className="absolute top-0 right-0 h-full w-[88%] max-w-[360px] bg-white shadow-2xl flex flex-col animate-[slideIn_0.22s_ease-out]"
            data-testid="mobile-nav-sheet"
          >
            <div className="flex items-center justify-between px-5 h-20 border-b border-[#E5E7EB]">
              <img src={LOGO_COLOR} alt="" aria-hidden="true" className="h-10 w-auto" />
              <button
                type="button"
                onClick={closeMobile}
                className="p-2 rounded-md text-[#373A45] hover:bg-[#F0E7F4]"
                aria-label="Închide meniul"
                data-testid="mobile-nav-close"
              >
                <X size={22} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-1 text-[15px] text-[#373A45]">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => goTo(l.id)}
                  className="text-left px-4 py-3 rounded-xl hover:bg-[#F0E7F4] hover:text-[#965EA7] transition-colors font-medium"
                  data-testid={`mobile-nav-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
            </nav>

            <div className="px-5 py-5 border-t border-[#E5E7EB] flex flex-col gap-3">
              <Button
                variant="outline"
                className="rounded-full border-2 border-[#373A45] text-[#373A45] hover:bg-[#373A45] hover:text-white font-semibold w-full"
                onClick={() => goTo("lead-form")}
                data-testid="mobile-nav-lead-cta"
              >
                {leadCta}
              </Button>
              <Button
                className="rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold shadow-[0_4px_14px_0_rgba(150,94,167,0.39)] w-full"
                onClick={() => goTo("pricing")}
                data-testid="mobile-nav-enroll-cta"
              >
                Înscrie-te
              </Button>
            </div>
          </div>
          <style>{`
            @keyframes slideIn {
              from { transform: translateX(100%); }
              to   { transform: translateX(0); }
            }
          `}</style>
        </div>
      )}
    </>
  );
}
